/** How each event kind and status looks: one icon and one label each, used by the list and the replay. */
import type { SessionEvent, SessionEventKind } from '@orgus/protocol';
import {
  Bot,
  CircleCheck,
  CircleDashed,
  CircleX,
  Hand,
  Layers,
  MessageSquareText,
  ShieldCheck,
  ShieldX,
  Sparkles,
  SquareTerminal,
  TriangleAlert,
  Wrench,
} from '@orgus/ui/icons';
import type { ComponentType } from 'react';
import { cn } from '@orgus/ui';

export const KIND_ICON: Record<SessionEventKind, ComponentType<{ className?: string }>> = {
  prompt: MessageSquareText,
  reply: Sparkles,
  tool: Wrench,
  subagent: Bot,
  approval: Hand,
  compaction: Layers,
  error: TriangleAlert,
  system: SquareTerminal,
};

export const KIND_SINGULAR: Record<SessionEventKind, string> = {
  prompt: 'Prompt',
  reply: 'Reply',
  tool: 'Tool call',
  subagent: 'Subagent',
  approval: 'Approval',
  compaction: 'Compaction',
  error: 'Error',
  system: 'System',
};

export const KIND_CLASS: Record<SessionEventKind, string> = {
  prompt: 'text-accent-text',
  reply: 'text-fg-muted',
  tool: 'text-fg-subtle',
  subagent: 'text-fg-muted',
  approval: 'text-status-attention',
  compaction: 'text-fg-subtle',
  error: 'text-status-danger',
  system: 'text-fg-subtle',
};

/** Track tick colours on the replay bar (tokens only). */
export const KIND_TICK: Record<SessionEventKind, string> = {
  prompt: 'bg-accent',
  reply: 'bg-fg-faint',
  tool: 'bg-border-strong',
  subagent: 'bg-working',
  approval: 'bg-waiting',
  compaction: 'bg-fg-subtle',
  error: 'bg-failed',
  system: 'bg-border',
};

type Status = NonNullable<SessionEvent['status']>;

const STATUS: Record<Status, { label: string; icon: ComponentType<{ className?: string }>; cls: string }> = {
  // Done and allowed are grey (status.md): colour is for running, needs you and failed.
  ok: { label: 'Done', icon: CircleCheck, cls: 'text-fg-subtle' },
  error: { label: 'Failed', icon: CircleX, cls: 'text-status-danger' },
  pending: { label: 'Running', icon: CircleDashed, cls: 'text-status-working' },
  denied: { label: 'Refused', icon: ShieldX, cls: 'text-status-danger' },
  allowed: { label: 'Allowed', icon: ShieldCheck, cls: 'text-fg-subtle' },
};

/** A status as icon plus label, never colour alone. Approvals that wait read "Waiting". */
export function EventStatus({ e, className }: { e: SessionEvent; className?: string }) {
  if (!e.status) return null;
  const s = STATUS[e.status];
  const label = e.status === 'pending' && e.kind === 'approval' ? 'Waiting' : s.label;
  const Icon = s.icon;
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 text-caption font-medium', e.status === 'pending' && e.kind === 'approval' ? 'text-status-attention' : s.cls, className)}>
      <Icon className="size-4" aria-hidden />
      {label}
    </span>
  );
}

export function KindIcon({ kind, className }: { kind: SessionEventKind; className?: string }) {
  const Icon = KIND_ICON[kind];
  return <Icon className={cn('size-4 shrink-0', KIND_CLASS[kind], className)} aria-hidden />;
}
